<template>
    <div id="map-container">
        <h1>Vitalità sociale e commerciale: dati dell'anno {{ this.year }}</h1>
        <div v-if="mapUrl">
            <iframe :src="mapUrl" width="100%" height="600px" allowfullscreen></iframe>
        </div>
    </div>

    <div class="legend-container" v-if="poisRanges">
        <h3>Interpretazione dei risultati</h3>
        <p>Gli score assegnati sono spiegati di seguito:</p>
        <table class="table">
            <thead>
                <tr>
                    <th></th>
                    <th id="step-a"><b>A</b></th>
                    <th id="step-b"><b>B</b></th>
                    <th id="step-c"><b>C</b></th>
                    <th id="step-d"><b>D</b></th>
                    <th id="step-e"><b>E</b></th>
                </tr>
            </thead>
            <tbody>
                <tr>
                    <td>Punti di ritrovo (% rispetto a tutta Bari)</td>
                    <td v-for="(range, index) in poisRanges" :key="index">
                        {{ range[1] == null ? 'score ≥ ' + range[0] + "%" : range.join('% ≤ score < ') + "%" }} </td>
                </tr>
                <tr>
                    <td>Attività commerciali (% rispetto a tutta Bari)</td>
                    <td v-for="(range, index) in poisRanges" :key="index">
                        {{ range[1] == null ? 'score ≥ ' + range[0] + "%" : range.join('% ≤ score < ') + "%" }} </td>
                </tr>
                <tr v-if="realEstateRanges">
                    <td>Dati immobiliare</td>
                    <td v-for="(range, index) in realEstateRanges" :key="index">
                        {{ range[0] == 0 ? range.join('% ≤ score ≤ ') + "%" : range.join('% < score ≤ ') + "%" }} </td>
                </tr>
            </tbody>
        </table>
    </div>

    <div class="year-selector-container">
        <h2><b>Visualizza i dati per l'anno</b></h2>
        <div id="year-selector">
            <select v-model="year">
                <option v-for="y in years" :key="y" :value="y">{{ y }}</option>
            </select>
            <!-- <button @click="getData" class="btn btn-primary" :disabled="loading">
                <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status"
                    aria-hidden="true"></span>
                {{ loading ? 'Caricamento...' : 'Aggiorna dati' }}
            </button> -->
        </div>
    </div>

    <hr>


    <div class="table-container">
        <h2>Dati aggregati per area</h2>
        <div v-if="loading" class="loading">
            <div class="loading-text">
                Caricamento...
            </div>
            <p class="spinner-border text-dark" role="status">
                <span class="visually-hidden">Loading...</span>
            </p>
        </div>
        <div v-else-if="rankingAreas">
            <div v-if="Object.keys(rankingAreas).length" class="table-category">
                <DataTable class="table table-bordered table-hover table-responsive pois-table" :options="options"
                    :key="areaTableKey">
                    <thead>
                        <tr>
                            <th>Posizione</th>
                            <th>Area</th>
                            <th>Punti di ritrovo (% rispetto a tutta Bari)</th>
                            <th>Attività commerciali (% rispetto a tutta Bari)</th>
                            <th v-if="realEstateRanges">Dati immobiliare</th>
                            <th>Indice di vitalità sociale e commerciale</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(data, area) in rankingAreas" :key="area">
                            <td>{{ data.position }}</td>
                            <td>{{ area }}</td>
                            <td>
                                <div v-if="data.meeting_places_score">
                                    <div>{{ data.meeting_places_score.percentage }}%</div>
                                    <div>
                                        <b>Score</b>: {{ data.meeting_places_score.score }}
                                    </div>
                                </div>
                                <div v-else>Nessun dato sui punti di ritrovo</div>
                            </td>
                            <td>
                                <div v-if="data.comm_activities_score">
                                    <div>{{ data.comm_activities_score.percentage }}%</div>
                                    <div>
                                        <b>Score</b>: {{ data.comm_activities_score.score }}
                                    </div>
                                </div>
                                <div v-else>Nessun dato sulle attività commerciali</div>
                            </td>
                            <td v-if="data.real_estate_score">
                                <div>{{ data.real_estate_score.percentage }}%</div>
                                <div>
                                    <b>Score</b>: {{ data.real_estate_score.score }}
                                </div>
                                <div class="pollutant-info">
                                    <span class="tab"></span>
                                    <a tabindex="0" class="btn btn-sm btn-info" role="button"
                                        data-bs-toggle="popover" data-bs-trigger="focus"
                                        :data-bs-title="`Dettagli immobiliare`" :data-bs-html="true"
                                        :data-bs-content="generatePopoverContent(data.real_estate_score)">
                                        <i class="bi bi-info-circle-fill"></i>
                                    </a>
                                </div>
                            </td>
                            <td class="final-index">
                                <div v-if="data.final_score != 'N/A'"><b>{{ data.final_score }}</b></div>
                                <div v-else>N/A</div>
                            </td>
                        </tr>
                    </tbody>
                </DataTable>
            </div>
            <p v-else>Non ci sono dati disponibili.</p>
        </div>
        <p v-else>Non ci sono dati disponibili.</p>
    </div>

    <hr>

    <div class="table-container">
        <h2>Dati per strada</h2>
        <div v-if="loading" class="loading">
            <div class="loading-text">
                Caricamento...
            </div>
            <p class="spinner-border text-dark" role="status">
                <span class="visually-hidden">Loading...</span>
            </p>
        </div>
        <div v-else-if="rankingStreets">
            <div v-if="Object.keys(rankingStreets).length">
                <div v-for="(streets, area) in rankingStreets" :key="area" class="table-category">
                    <h3>{{ area }}</h3>
                    <div v-if="Object.keys(streets).length">
                        <DataTable class="table table-bordered table-hover table-responsive pois-table"
                            :key="streetTableKey" :options="options">
                            <thead>
                                <tr>
                                    <th>Posizione</th>
                                    <th>Area</th>
                                    <th>Punti di ritrovo (% rispetto a tutta Bari)</th>
                                    <th>Attività commerciali (% rispetto a tutta Bari)</th>
                                    <th v-if="realEstateRanges">Dati immobiliare</th>
                                    <th>Indice di vitalità sociale e commerciale</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(data, street) in streets" :key="street">
                                    <td>{{ data.street_position }}</td>
                                    <td>{{ street }}</td>
                                    <td>
                                        <div v-if="data.meeting_places_score">
                                            <div>{{ data.meeting_places_score.percentage }}%</div>
                                            <div>
                                                <b>Score</b>: {{ data.meeting_places_score.score }}
                                            </div>
                                        </div>
                                        <div v-else>Nessuna dato sui punti di ritrovo</div>
                                    </td>
                                    <td>
                                        <div v-if="data.comm_activities_score">
                                            <div>{{ data.comm_activities_score.percentage }}%</div>
                                            <div>
                                                <b>Score</b>: {{ data.comm_activities_score.score }}
                                            </div>
                                        </div>
                                        <div v-else>Nessuna dato sulle attività commerciali</div>
                                    </td>
                                    <td v-if="data.real_estate_score">
                                        <div>{{ data.real_estate_score.percentage }}%</div>
                                        <div>
                                            <b>Score</b>: {{ data.real_estate_score.score }}
                                        </div>
                                        <div class="pollutant-info">
                                            <span class="tab"></span>
                                            <a tabindex="0" class="btn btn-sm btn-info" role="button"
                                                data-bs-toggle="popover" data-bs-trigger="focus"
                                                :data-bs-title="`Dettagli immobiliare`" :data-bs-html="true"
                                                :data-bs-content="generatePopoverContent(data.real_estate_score)">
                                                <i class="bi bi-info-circle-fill"></i>
                                            </a>
                                        </div>
                                    </td>
                                    <td class="final-index">
                                        <div v-if="data.final_score != 'N/A'"><b>{{ data.final_score }}</b></div>
                                        <div v-else>N/A</div>
                                    </td>
                                </tr>
                            </tbody>
                        </DataTable>
                    </div>
                    <p v-else>Non ci sono dati disponibili.</p>
                </div>
            </div>
        </div>
        <p v-else>Non ci sono dati disponibili.</p>
    </div>


</template>


<script>
import DataTable from 'datatables.net-vue3';
import DataTablesCore from 'datatables.net';
import 'datatables.net-responsive';
import 'datatables.net-select';
import { useHead } from "@vueuse/head";

DataTable.use(DataTablesCore);

export default {
    components: { DataTable },
    setup() {
        useHead({
            title: 'InfoNav - Vitalità sociale e commerciale'
        });
    },
    data() {
        return {
            loading: false,
            mapUrl: null,
            poisRanges: null,
            realEstateRanges: null,
            year: new Date().getFullYear(),
            forceOverwrite: true,
            rankingAreas: null,
            rankingStreets: null,
            options: {
                responsive: true,
                select: false,
                order: [[0, 'asc']],
                language: {
                    url: 'https://cdn.datatables.net/plug-ins/1.13.1/i18n/it-IT.json'
                },
                drawCallback: function () {
                    const final_index_td = document.getElementsByClassName("final-index");
                    for (let i = 0; i < final_index_td.length; i++) {
                        let inner_text = final_index_td[i].innerText.trim();
                        if (inner_text == "A") {
                            final_index_td[i].classList.add('index-a');
                        } else if (inner_text == "B") {
                            final_index_td[i].classList.add('index-b');
                        } else if (inner_text == "C") {
                            final_index_td[i].classList.add('index-c');
                        } else if (inner_text == "D") {
                            final_index_td[i].classList.add('index-d');
                        } else if (inner_text == "E") {
                            final_index_td[i].classList.add('index-e');
                        }
                    }
                }
            },
            years: [2023, 2024, 2025],
        }
    },
    watch: {
        year(newYear) {
            console.log('Year changed to:', newYear);
            this.poisRanges = null;
            this.realEstateRanges = null;
            this.rankingAreas = null;
            this.rankingStreets = null;
            this.getData();
        }
    },
    methods: {
        generatePopoverContent(data) {
            const formatValue = (label, value) =>
                `<b>${label}</b>: ${value} €/m<sup>2</sup><br>`;

            let html = "<u><b>Valori complessivi</b></u><br>";
            const overall = data.overall.total;

            html += formatValue("Prezzo minimo", overall.min_on_min);
            html += formatValue("Prezzo massimo", overall.max_on_max);
            html += formatValue("Mediana su minimo", overall.median_on_min);
            html += formatValue("Mediana su massimo", overall.median_on_max);
            html += "<br><u><b>Dettaglio per tipologia</b></u><br>";

            const types = data.types.total;

            for (const [type, values] of Object.entries(types)) {
                html += `<i>${type}</i><br>`;
                html += formatValue("– Prezzo minimo", values.min_on_min);
                html += formatValue("– Prezzo massimo", values.max_on_max);
                html += formatValue("– Mediana su minimo", values.median_on_min);
                html += formatValue("– Mediana su massimo", values.median_on_max);
                html += "<br>";
            }

            return html;
        },
        async getData() {
            console.log('Fetching data for year:', this.year);
            this.loading = true;
            if (this.year == new Date().getFullYear()) {
                this.forceOverwrite = true;
            }
            else {
                this.forceOverwrite = false;
            }
            try {
                const response = await fetch(`/api/scv/?year=${this.year}&force_overwrite=${this.forceOverwrite}`, {
                    method: 'GET',
                });

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const data = await response.json();

                await this.$nextTick();
                this.poisRanges = data['pois_ranges'];
                if (Object.keys(data).includes('real_estate_ranges')) {
                    this.realEstateRanges = data['real_estate_ranges'];
                }
                
                if (Object.keys(data).includes('year')) {
                    this.year = data['year'];
                } else {
                    this.year = "2025";
                }
                this.rankingAreas = data['final_rank_areas'];
                this.rankingStreets = data['final_rank_streets'];
                this.mapUrl = data['map_url'] + "?year=" + this.year;
            } catch (error) {
                console.error('Error fetching data:', error);
            } finally {
                this.loading = false;
            }
        }
    },
    computed: {
        areaTableKey() {
            return `area-${this.year}-${this.dataVersion}`;
        },
        streetTableKey() {
            return `street-${this.year}-${this.dataVersion}`;
        }
    },
    mounted() {
        this.getData();
        // Initialize popovers after data is loaded
        this.$nextTick(() => {
            const popoverTriggerList = document.querySelectorAll('[data-bs-toggle="popover"]');
            [...popoverTriggerList].map(popoverTriggerEl => new window.bootstrap.Popover(popoverTriggerEl, {
                html: true
            }));
        });
    },
    updated() {
        // Re-initialize popovers after data updates
        this.$nextTick(() => {
            const popoverTriggerList = document.querySelectorAll('[data-bs-toggle="popover"]');
            [...popoverTriggerList].map(popoverTriggerEl => new window.bootstrap.Popover(popoverTriggerEl, {
                html: true
            }));
        });
    }
}
</script>


<style scoped>
body {
    display: flex;
    flex-direction: column;
    font-family: "Poppins", sans-serif !important;
    font-size: large !important;
}

ul {
    display: flex;
    justify-content: space-evenly;
    padding-left: 0%;
    margin: 0;
    width: -webkit-fill-available;
}

li {
    list-style: none;
    width: -webkit-fill-available;
    text-align: center;
}

li.nav-item:hover {
    background-color: #b7defb;
    border-radius: var(--bs-nav-pills-border-radius);
}

.nav-pills .nav-link.active,
.nav-pills .show>.nav-link {
    color: white !important;
}

a {
    margin-left: auto;
    text-decoration: none !important;
}

#key p {
    margin: 0;
    margin-left: 1%;
    margin-right: 1%;
    margin-top: 1%;
}

#key h4 {
    margin: 0;
    margin-top: 1%;
}


#color-way {
    height: 22px;
    width: 22px;
}

#n-accident p {
    font-size: smaller;
}

html,
body {
    margin: 0;
    padding: 0;
}

@media (max-height: 40em) {
    h1 {
        font-size: 1.5em;
    }
}


/* Best */
#step-a {
    background-color: deepskyblue;
    color: black;
    border: solid black;
    border-width: 2px 1px 2px 2px;
}

#step-b {
    background-color: lightgreen;
    color: black;
    border: solid black;
    border-width: 2px 1px 2px 2px;
}

#step-c {
    background-color: yellow;
    color: black;
    border: solid black;
    border-width: 2px 1px 2px 2px;
}

#step-d {
    background-color: orange;
    color: black;
    border: solid black;
    border-width: 2px 1px 2px 2px;
}

/* Worst */
#step-e {
    background-color: lightcoral !important;
    color: black;
    border: solid black;
    border-width: 2px 2px 2px 2px;
}

/* Best */
.index-a {
    background-color: deepskyblue !important;
}

.index-b {
    background-color: lightgreen !important;
}

.index-c {
    background-color: yellow !important;
}

.index-d {
    background-color: orange !important;
}

/* Worst */
.index-e {
    background-color: lightcoral !important;
}


#scores {
    width: 100%;
    display: flex;
    border: 1px solid;
}

/* Header */
.header {
    background-color: #0000FF;
    color: white;
    padding: 20px;
    text-align: center;
    margin-bottom: 20px;
}

.green-column {
    background-color: #96adff !important;
}

.table-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-top: 30px;
}

.table-category {
    margin-bottom: 40px;
}

.table-category h3 {
    text-align: center;
    margin-bottom: 15px;
    color: #007bff;
}

.table th,
.table td {
    text-align: center;
    vertical-align: middle;
}

.table thead th {
    background-color: #007bff;
    color: white;
    font-weight: bold;
}

.legend-container {
    background-color: #f8f9fa;
    border-radius: 8px;
    padding: 15px;
    margin: 30px auto;
}

.legend-container h3 {
    text-align: center;
    color: #007bff;
    margin-bottom: 20px;
}

#map-container {
    display: flex;
    flex-direction: column;
    margin-top: 30px;
}

#map-container h1 {
    text-align: center;
    margin-bottom: 20px;
    color: #007bff;
}

.pollutant-info {
    display: flex;
    align-items: center;
    margin-bottom: 0.5rem;
}

.pollutant-info span {
    display: inline-block;
}

.loading {
    display: flex;
    flex-direction: column;
    align-items: center;
}

.loading-text {
    margin-bottom: 1rem;
}

.year-selector-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin: 20px 0;
}

.year-selector-container h2 {
    margin-bottom: 15px;
}

#year-selector {
    display: flex;
    align-items: center;
    gap: 10px;
}
</style>